import { useEffect, useState } from "react";
import { Share, PlusSquare, Smartphone, Zap, Target, TrendingUp, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LOGO_ICON } from "@/components/common";

const DISMISS_KEY = "sb_install_dismissed_at";
const DISMISS_MS = 3 * 24 * 3600 * 1000;

export const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true || localStorage.getItem("sb_installed") === "1";
export const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const isMobile = () => /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent) || window.innerWidth < 768;

export function usePWAInstall() {
  const [promptEvt, setPromptEvt] = useState(window.__sbInstallPrompt || null);
  useEffect(() => {
    const onAvail = () => setPromptEvt(window.__sbInstallPrompt);
    const onInstalled = () => { localStorage.setItem("sb_installed", "1"); setPromptEvt(null); };
    window.addEventListener("sb-installable", onAvail);
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("sb-installable", onAvail); window.removeEventListener("appinstalled", onInstalled); };
  }, []);
  const install = async () => {
    if (!promptEvt) return false;
    promptEvt.prompt();
    const choice = await promptEvt.userChoice;
    if (choice?.outcome === "accepted") localStorage.setItem("sb_installed", "1");
    window.__sbInstallPrompt = null;
    setPromptEvt(null);
    return true;
  };
  return { canPrompt: !!promptEvt, install, standalone: isStandalone(), ios: isIOS() };
}

const BENEFITS = [
  [Smartphone, "Open Sidebizz like an app"],
  [Zap, "Faster access to your business journey"],
  [Target, "Stay focused on your next steps"],
  [TrendingUp, "Track your progress easily"],
];

export function InstallSheet({ open, onClose, onDismiss }) {
  const { canPrompt, install, ios } = usePWAInstall();
  const [showSteps, setShowSteps] = useState(false);
  const handleAdd = async () => {
    if (canPrompt && (await install())) return onClose();
    setShowSteps(true);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && (onDismiss || onClose)()}>
      <DialogContent className="max-w-md rounded-3xl border-[#EADEC9] bg-[#FBF8F2] p-0 overflow-hidden [&>button]:hidden" data-testid="pwa-install-modal">
        <div className="relative bg-[#1E2B3C] px-6 pt-8 pb-10 text-white">
          <button onClick={onDismiss || onClose} className="absolute right-4 top-4 h-9 w-9 rounded-full bg-white/10 flex items-center justify-center" data-testid="pwa-install-close-btn"><X className="h-4 w-4" /></button>
          <img src={LOGO_ICON} alt="Sidebizz" className="h-16 w-16 rounded-2xl shadow-lg pop-in" />
          <DialogTitle className="mt-5 text-2xl font-extrabold font-display text-white">Get the best Sidebizz experience</DialogTitle>
          <DialogDescription className="mt-2 text-sm text-white/70 leading-relaxed">
            Add Sidebizz to your Home Screen for faster access and a more app-like experience while you build your business.
          </DialogDescription>
        </div>
        <div className="px-6 py-6 space-y-5">
          {!showSteps ? (
            <ul className="space-y-3 stagger">
              {BENEFITS.map(([Icon, txt]) => (
                <li key={txt} className="flex items-center gap-3 text-sm font-medium text-[#1E2B3C]">
                  <span className="h-9 w-9 rounded-xl bg-[#E8F8F4] text-[#1DB99A] flex items-center justify-center"><Icon className="h-4 w-4" /></span>{txt}
                </li>
              ))}
            </ul>
          ) : (
            <ol className="space-y-3" data-testid="pwa-install-steps">
              {(ios ? [[Share, "Tap the Share icon in Safari"], [PlusSquare, "Select “Add to Home Screen”"], [Smartphone, "Tap “Add”"]]
                : [[Share, "Open your browser menu (⋮)"], [PlusSquare, "Select “Install app” or “Add to Home screen”"], [Smartphone, "Confirm “Install”"]])
                .map(([Icon, txt], i) => (
                  <li key={txt} className="flex items-center gap-3 sb-card p-3 text-sm font-medium">
                    <span className="h-8 w-8 rounded-full bg-[#1DB99A] text-white font-bold flex items-center justify-center">{i + 1}</span>
                    <span className="flex-1">{txt}</span><Icon className="h-5 w-5 text-[#1DB99A]" />
                  </li>
                ))}
            </ol>
          )}
          <div className="space-y-2 pb-safe">
            {!showSteps && <button className="sb-btn-primary w-full" onClick={handleAdd} data-testid="pwa-add-home-btn">Add Sidebizz to Home Screen</button>}
            <button className="w-full min-h-[44px] text-sm font-semibold text-[#5C6B73]" onClick={onDismiss || onClose} data-testid="pwa-continue-browser-btn">
              {showSteps ? "Done" : "Continue in browser"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function InstallPromptGate() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (isStandalone() || !isMobile()) return;
    const last = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (Date.now() - last < DISMISS_MS) return;
    const id = setTimeout(() => setOpen(true), 900);
    return () => clearTimeout(id);
  }, []);
  const dismiss = () => { localStorage.setItem(DISMISS_KEY, String(Date.now())); setOpen(false); };
  return <InstallSheet open={open} onClose={dismiss} onDismiss={dismiss} />;
}
